import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Text,
  Title,
  List,
  Group,
  Loader,
  Stack,
  Container,
  Button,
  TextInput,
  Transition,
  Center,
  ActionIcon,
  Badge,
} from "@mantine/core";
import { DatePickerInput, DatesProvider } from "@mantine/dates";
import "dayjs/locale/hr";
import { db } from "../firebase";
import { ref, onValue, set, update } from "firebase/database";
import { getAuth, signInAnonymously } from "firebase/auth";
import { Order } from "models/models";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FIREBASE_DB_ROOT } from "../App";
import { sendDeliveryStartedNotification } from "services/sendSms";
import { sendOrderNotification } from "services/sendFcm";

dayjs.extend(customParseFormat);

const AdminOnly = ({ children }: { children: React.ReactNode }) => {
  const isAdmin = localStorage.getItem("admin") === "true";
  if (!isAdmin) {
    return null;
  }
  return <>{children}</>;
};

const ManagerOnly = ({ children }: { children: React.ReactNode }) => {
  const isManager = localStorage.getItem("manager") === "true";
  const isAdmin = localStorage.getItem("admin") === "true";
  if (!isManager && !isAdmin) {
    return null;
  }

  return <>{children}</>;
};

const ordersByPlatform = (orders: Order[]) => {
  if (!orders || orders.length === 0) {
    return null;
  }

  const stats = {
    Android: { new: 0, old: 0 },
    iOS: { new: 0, old: 0 },
    Unknown: 0,
  };

  orders.forEach((order) => {
    const os = order.DeviceInfo?.OS;

    if (os === "Android") {
      const versionCode =
        typeof order.DeviceInfo?.versionCode === "string"
          ? parseInt(order.DeviceInfo?.versionCode)
          : (order.DeviceInfo?.versionCode ?? 0);
      const isNew = versionCode >= 87;
      isNew ? stats.Android.new++ : stats.Android.old++;
    } else if (os === "iOS") {
      const versionCode =
        typeof order.DeviceInfo?.versionCode === "string"
          ? parseInt(order.DeviceInfo?.versionCode)
          : (order.DeviceInfo?.versionCode ?? 0);
      const isNew = versionCode >= 186;
      isNew ? stats.iOS.new++ : stats.iOS.old++;
    } else {
      stats.Unknown++;
    }
  });

  const percentNew = (platform: { new: number; old: number }) => {
    const total = platform.new + platform.old;
    return total === 0 ? "0.0" : ((platform.new / total) * 100).toFixed(1);
  };

  const totalOrders = (platform: { new: number; old: number }) =>
    platform.new + platform.old;

  return (
    <Text size="sm" color="dimmed">
      {`Android: ${totalOrders(stats.Android)} — ${stats.Android.new}/${
        stats.Android.old
      } (${percentNew(stats.Android)}% new)`}{" "}
      <br />
      {`iOS: ${totalOrders(stats.iOS)} — ${stats.iOS.new}/${
        stats.iOS.old
      } (${percentNew(stats.iOS)}% new)`}{" "}
      <br />
      {`Unknown: ${stats.Unknown}`}
    </Text>
  );
};

const getCurrentUsername = () => {
  return getAuth().currentUser?.email?.split("@")[0] || "";
};

export const Orders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyDelivery, setShowOnlyDelivery] = useState(true);
  const [searchMode, setSearchMode] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [highlightedOrder, setHighlightedOrder] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const orderRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const navigate = useNavigate();

  const path = `${FIREBASE_DB_ROOT}/Narudžbe/${selectedDate.getFullYear()}/${
    selectedDate.getMonth() + 1
  }/${selectedDate.getDate()}`;
  const ordersRef = ref(db, path);

  const pushDeliveredBy = async (order: Order) => {
    if (!order || !order.OrderId) return;
    if (order.DeliveredBy) return; // Already started

    const deliveredBy = getCurrentUsername();
    const startedAt = dayjs().format("DD.MM.YYYY HH:mm");

    // Update only DeliveredBy in the database (marking as "In Progress")
    const orderRef = ref(db, `${path}/${order.OrderId}`);

    try {
      await update(orderRef, {
        DeliveredBy: deliveredBy,
        StartedAt: startedAt,
      });

      // Send FCM notiication
      await sendOrderNotification(
        order.OrderId,
        dayjs().format("YYYY-MM-DD"),
        order.DeviceInfo?.language,
        FIREBASE_DB_ROOT,
      );

      // Send SMS notification
      //await sendDeliveryStartedNotification(order.Broj, {
      //  ime: order.Ime,
      //  adresa: order.Adresa,
      //});
    } catch (error) {
      console.error("Error updating order or sending SMS:", error);
      showNotification({
        title: "Greška",
        message: "Pogreška kod slanja SMS-a",
        color: "red",
      });
    }
  };

  const pushDeliveredAt = (order: Order) => {
    if (!order || !order.OrderId) return;
    if (order.DeliveredAt) return; // Already delivered

    const deliveredAt = dayjs().format("DD.MM.YYYY HH:mm");

    // Update only DeliveredAt in the database (finishing the delivery)
    const orderRef = ref(db, `${path}/${order.OrderId}`);
    update(orderRef, {
      DeliveredAt: deliveredAt,
    }).catch((error: any) => {
      console.error("Error updating order:", error);
    });
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "auto",
    });
  };

  const logout = () => {
    const auth = getAuth();
    auth.signOut().then(() => {
      localStorage.removeItem("loginTimestamp");
      localStorage.removeItem("admin");
      localStorage.removeItem("manager");

      showNotification({
        title: "Odjava",
        message: "Uspješno ste se odjavili.",
        color: "green",
      });

      navigate("/login", { replace: true });
    });
  };

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !user.email) {
      console.warn("No logged in user — this should never happen");

      auth.signOut();
      return;
    }

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setOrders([]);
        setLoading(false);
        return;
      }

      // add OrderId to each order
      const parsedData = Object.entries(data).map(([key, value]) => ({
        ...(value as Order),
        OrderId: key,
      }));

      const isAdmin = localStorage.getItem("admin") === "true";

      const filteredOrders = parsedData.filter((order) => {
        // Exclude orders without time
        if (!order.Vrijeme) return false;

        // Admins see all orders, others see only received ones
        if (isAdmin) return true;

        return order.Recieved === true;
      });

      setOrders(filteredOrders);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedDate]);

  const parseEuropeanDate = (dateStr: string): Date => {
    const match = dateStr.match(/\d+/g);
    if (!match || match.length < 5) throw new Error("Invalid date format");

    const [day, month, year, hour, minute] = match.map(Number);
    return new Date(year, month - 1, day, hour, minute);
  };

  const scrollToOrder = (orderNumber: number) => {
    const orderElement = orderRefs.current.get(orderNumber);
    if (orderElement) {
      orderElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedOrder(orderNumber); // Highlight the order
    } else {
      showNotification({
        title: "Narudžba nije pronađena",
        message: `Nema narudžbe s brojem #${orderNumber}`,
        color: "red",
      });
    }
  };

  const handleSearchSubmit = () => {
    const number = parseInt(searchValue);
    if (!isNaN(number)) {
      scrollToOrder(number);
    }
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
    setLoading(true);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
    setLoading(true);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setLoading(true);
  };

  const isToday = dayjs(selectedDate).isSame(dayjs(), "day");

  return (
    <Container size="md" py="md" style={{ position: "relative" }}>
      <Group gap="1rem" align="center" mb="lg">
        <Title order={3}>Narudžbe</Title>
        <Button
          variant="outline"
          size="xs"
          onClick={() => setShowOnlyDelivery((prev) => !prev)}
        >
          {showOnlyDelivery ? "Prikaži sve" : "Samo dostava"}
        </Button>
        <AdminOnly>
          <Button variant="outline" size="xs" onClick={() => navigate("/map")}>
            Mapa
          </Button>
        </AdminOnly>
      </Group>

      <DatesProvider settings={{ locale: "hr", firstDayOfWeek: 1 }}>
        <AdminOnly>
          <Group gap="xs" align="center" mb="md">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={goToPreviousDay}
              title="Prethodni dan"
            >
              ◀
            </ActionIcon>
            <DatePickerInput
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setLoading(true);
                }
              }}
              valueFormat="DD.MM.YYYY"
              size="sm"
              style={{ width: 140 }}
            />
            <ActionIcon
              variant="default"
              size="lg"
              onClick={goToNextDay}
              title="Sljedeći dan"
              disabled={isToday}
            >
              ▶
            </ActionIcon>
            {!isToday && (
              <Button variant="light" size="xs" onClick={goToToday}>
                Danas
              </Button>
            )}
          </Group>
        </AdminOnly>
      </DatesProvider>

      <Stack
        gap="sm"
        mb="lg"
        style={{ visibility: loading ? "hidden" : "visible" }}
      >
        <Text size="sm" color="dimmed">
          {`${
            orders.filter((order) => order.Adresa !== "PREUZIMANJE U OBJEKTU")
              .length
          } dostava, ${
            orders.filter((order) => order.Adresa === "PREUZIMANJE U OBJEKTU")
              .length
          } preuzimanja, ${orders.length} ukupno`}
        </Text>

        <AdminOnly>{ordersByPlatform(orders)}</AdminOnly>

        <Group gap="sm">
          <Text size="sm" color="dimmed">
            Korisnik <strong>{getCurrentUsername()}</strong>
          </Text>
          <Text
            size="sm"
            color="blue"
            td={"underline"}
            onClick={logout}
            style={{ cursor: "pointer" }}
          >
            Odjava
          </Text>
          <ManagerOnly>
            <Text size="sm" color="dimmed">
              <Link to="/stats" style={{ textDecoration: "underline" }}>
                Statistika
              </Link>
            </Text>
          </ManagerOnly>
          <ManagerOnly>
            <Text size="sm" color="dimmed">
              <Link to="/settings" style={{ textDecoration: "underline" }}>
                Postavke
              </Link>
            </Text>
          </ManagerOnly>
        </Group>
      </Stack>

      {loading ? (
        <Center>
          <Loader variant="dots" />
        </Center>
      ) : orders.length > 0 ? (
        <Stack gap="lg">
          {orders
            .filter((order) =>
              showOnlyDelivery
                ? order.Adresa !== "PREUZIMANJE U OBJEKTU"
                : true,
            )
            .filter((order) => {
              if (!showOnlyDelivery) {
                return true;
              }
              // show only orders with DeliveredAt less than 15 minutes ago
              if (order.DeliveredAt) {
                const deliveredAt = dayjs(
                  order.DeliveredAt,
                  "DD.MM.YYYY HH:mm",
                );
                return deliveredAt.isAfter(dayjs().subtract(15, "minutes"));
              }
              return true; // Show all orders if not delivered
            })
            .map((order, index) => (
              <Card
                key={index}
                shadow="md"
                padding="lg"
                radius="md"
                withBorder
                ref={(el) => {
                  if (el) {
                    orderRefs.current.set(order.OrderCount, el);
                  }
                }}
                style={{
                  border:
                    highlightedOrder === order.OrderCount
                      ? "2px solid red"
                      : "",
                  transition: "border 0.3s ease",
                }}
              >
                <AdminOnly>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                      borderWidth: "0 32px 32px 0",
                      borderColor: `transparent ${
                        order.Recieved
                          ? "#15b933da" // green
                          : order.Canceled
                            ? "#ff0c0cff" // red
                            : "#171313ff" // pending (dark gray)
                      } transparent transparent`,
                      borderTopRightRadius: "8px",
                    }}
                  />
                </AdminOnly>
                <Group mb="xs">
                  <Title order={3}>
                    #{order.OrderCount} {order.Ime}
                  </Title>
                </Group>
                <Stack>
                  {!order.Broj.includes("PREUZIMANJE") && (
                    <Text
                      size="sm"
                      color="blue"
                      component="a"
                      href={`tel:${order.Broj}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📞 {order.Broj}
                    </Text>
                  )}
                  {order.Adresa === "PREUZIMANJE U OBJEKTU" ? (
                    <Text size="sm">PREUZIMANJE {order.Preuzimanje}</Text>
                  ) : (
                    <Text
                      size="sm"
                      color="blue"
                      component="a"
                      href={`geo:0,0?q=${encodeURIComponent(
                        `${order.Adresa} ${order.Mjesto ?? "Solin"}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📍 {order.Adresa} {order.Mjesto ?? ""}
                    </Text>
                  )}
                  <Text size="sm" color="dimmed">
                    {parseEuropeanDate(order.Vrijeme).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </Text>
                </Stack>

                <Title order={5} mt="sm">
                  Artikli
                </Title>
                <List spacing="xs" size="sm" withPadding>
                  {order.Artikli &&
                    Object.entries(order.Artikli).map(([key, item]: any) => (
                      <List.Item key={key}>
                        <Text fw={500}>{item.Ime}</Text>
                        <Text size="xs" color="dimmed">
                          {item.Prilozi}
                        </Text>
                      </List.Item>
                    ))}
                </List>
                <AdminOnly>
                  {order.Total && (
                    <Text size="md" mt="sm" fw={600}>
                      Ukupno: €{order.Total.toFixed(2)}
                    </Text>
                  )}
                </AdminOnly>

                <AdminOnly>
                  {order.DeviceInfo && (
                    <Text size="xs" color="dimmed" mt="sm">
                      {order.DeviceInfo.OS}{" "}
                      {order.DeviceInfo.osVersion ||
                        order.DeviceInfo.androidVersion ||
                        ""}{" "}
                      {order.DeviceInfo.language
                        ? ` - ${order.DeviceInfo.language}`
                        : ""}
                      {order.DeviceInfo.versionCode
                        ? ` - ${order.DeviceInfo.versionCode}`
                        : ""}
                      {order.DeviceInfo.versionName
                        ? ` - ${order.DeviceInfo.versionName}`
                        : ""}
                    </Text>
                  )}
                </AdminOnly>
                <Group
                  mt={"md"}
                  justify="flex-end"
                  // Show only if order is for today and received
                  style={{
                    display: !isToday || !order.Recieved ? "none" : "flex",
                  }}
                >
                  {!order.DeliveredBy ? (
                    // Step 1: Show green "Kreni" button when not started
                    <Button
                      color="orange.8"
                      style={{
                        display: order.Adresa.includes("PREUZIMANJE")
                          ? "none"
                          : "inline-block",
                      }}
                      variant="filled"
                      size="sm"
                      onClick={() => pushDeliveredBy(order)}
                    >
                      Kreni
                    </Button>
                  ) : !order.DeliveredAt ? (
                    // Step 2: Show "U tijeku" status with "Završi" button when started but not finished
                    <Button
                      color="blue"
                      style={{
                        display: order.Adresa.includes("PREUZIMANJE")
                          ? "none"
                          : "inline-block",
                      }}
                      variant="filled"
                      size="sm"
                      onClick={() => pushDeliveredAt(order)}
                      disabled={order.DeliveredBy !== getCurrentUsername()}
                    >
                      {`U tijeku (${order.DeliveredBy}) - Završi`}
                    </Button>
                  ) : (
                    // Step 3: Show completed status when both are set
                    <Button
                      color="green"
                      style={{
                        display: order.Adresa.includes("PREUZIMANJE")
                          ? "none"
                          : "inline-block",
                      }}
                      variant="filled"
                      size="sm"
                    >
                      {`Dostavljeno ${dayjs(
                        order.DeliveredAt,
                        "DD.MM.YYYY HH:mm",
                      ).format("HH:mm")} (${order.DeliveredBy})`}
                    </Button>
                  )}
                </Group>
              </Card>
            ))}
        </Stack>
      ) : (
        <Text ta="center" color="dimmed">
          Nema narudžbi za danas.
        </Text>
      )}

      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1000,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <Transition
          mounted={searchMode}
          transition="pop"
          duration={200}
          timingFunction="ease"
        >
          {(styles) => (
            <TextInput
              type="number"
              style={{ ...styles }}
              placeholder="Broj"
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit();
                  setSearchMode(false);
                  setSearchValue("");
                }
              }}
              size="xs"
              w={80}
              styles={{ input: { fontSize: "16px" } }}
              autoFocus
            />
          )}
        </Transition>

        {searchMode ? (
          <Button
            variant="filled"
            size="xs"
            onClick={() => {
              handleSearchSubmit();
              setSearchMode(false);
              setSearchValue("");
            }}
          >
            OK
          </Button>
        ) : (
          <Button
            radius="xl" // Makes the button circular
            variant="default"
            size="md"
            onClick={() => setSearchMode(true)}
            style={{
              width: 40,
              height: 40,
              padding: 0, // Ensure no extra padding is added
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            🔍
          </Button>
        )}
      </div>
      {/* 
      <Button
        radius="xl"
        variant="default"
        size="md"
        onClick={scrollToBottom}
        style={{
          position: "fixed",
          bottom: "16px",
          right: "16px",
          zIndex: 1000,
          width: 40,
          height: 40,
          padding: 0, // Ensure no extra padding is added
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        ⬇️
      </Button>
      */}
    </Container>
  );
};
